import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../Controllers/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../Controllers/Store";
import { hasPermission } from "../../Controllers/Selectors/permissionsSelector";

import { Toast } from "primereact/toast";
import { Ripple } from "primereact/ripple";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { Image } from "primereact/image";
import { motion, AnimatePresence } from 'framer-motion';

import GoBackButton from "../../Components/GoBackButton";
import PageLoader from "../../Components/PageLoader";
import ErrorPage from "../../Components/ErrorPage";
import NoDataCreated from "../../Components/NoDataCreated";
import TableButton from "../../Components/TableButton";
import DataListModalSearch from "../../Components/DataListModal/DataListModalSearch";
import DataListModalClose from "../../Components/DataListModal/DataListModalClose";
import DataException from "../../Components/DataException";
import CustomTableLoader from "../../Components/CustomTableLoader";
import TableActionLoader from "../../Components/TableActionLoader";
import { goToTop } from "../../Components/goToTop";

import { Project } from "../../Controllers/Types/Project/ProjectInterface";
import { Employee } from "../../Controllers/Types/EmployeeInterface";
import { BuildingControlPermissionEmployee, BuildingControlPermissionPayload, BuildingControlPermission as BuildingControlPermissionType } from "../../Controllers/Types/BuildingControl/BuildingControlPermissionType";
import { fetchAllProjects } from "../../Controllers/Thunks/projectThunks";
import { fetchBuildingControlPermissions } from "../../Controllers/Thunks/BuildingControlThunks";
import { addBuildingControlEmployeesAPI, addBuildingControlProjectsAPI, addBuildingControlPermissionAPI, removeBuildingControlProjectsAPI, removeBuildingControlEmployeeAPI } from "../../Api/BuildingControlService/BuildingControlService";
import { fetchAllContact } from "../../Controllers/Thunks/contactThunks";
import { confirmDialog } from "primereact/confirmdialog";
import { Tooltip } from "primereact/tooltip";

const BuildingControlPermission: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const employeeListBodyRef = useRef<HTMLDivElement>(null);

    const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [removeProjectLoadingIdx, setRemoveProjectLoadingIdx] = useState<number | null>(null);
    const [removeEmployeeLoadingIdx, setRemoveEmployeeLoadingIdx] = useState<number | null>(null);
    const [saveLoading, setSaveLoading] = useState<boolean>(false);
    const [bodyHeight, setBodyHeight] = useState<number | "auto">("auto");

    // BC Project
    const [showBCProjectModal, setShowBCProjectModal] = useState<boolean>(false);
    const [selectedBCProjects, setSelectedBCProjects] = useState<Project[]>([]);
    const [bcProjectSearchKey, setBcProjectSearchKey] = useState<string>('');

    // BC Employee
    const [showEmployeeModal, setShowEmployeeModal] = useState<boolean>(false);
    const [selectedEmployees, setSelectedEmployees] = useState<Employee[]>([]);
    const [employeeSearchKey, setEmployeeSearchKey] = useState<string>('');

    const [currentBCProjectPermission, setCurrentBCProjectPermission] = useState<BuildingControlPermissionType | null>(null);
    const [activePermissionEmployee, setActivePermissionEmployee] = useState<BuildingControlPermissionEmployee | null>(null);

    // Permission draft: Map<bcp_employee_id, Map<service_id, {can_view, can_edit}>>
    const [permissionDraft, setPermissionDraft] = useState<Map<number, Map<number, { can_view: 1 | 0; can_edit: 1 | 0 }>>>(new Map());

    // State data
    const { allProjects: projects, loading: loadingProjects, error: errorProjects } = useAppSelector((state) => state.project);
    const { employees, loading: loadingEmployees, error: errorEmployees } = useAppSelector((state) => state.contact)

    const {
        loadingBuildingControlPermissions, errorBuildingControlPermissions, buildingControlPermissions,
    } = useAppSelector((state) => state.buildingControl);

    /* Permissions */
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const hasPermissionForAddBuildingControlPermission = useCanEdit("Building Control Permission", "add_building_control_permission");
    const hasPermissionForAddBuildingControlProject = useCanEdit("Building Control Permission", "add_building_control_project");
    const hasPermissionForRemoveBuildingControlProject = useCanEdit("Building Control Permission", "remove_building_control_project");
    const hasPermissionForAssignBuildingControlEmployee = useCanEdit("Building Control Permission", "assign_building_control_employee");
    const hasPermissionForRemoveBuildingControlEmployee = useCanEdit("Building Control Permission", "remove_building_control_employee");
    /*  */

    const filteredBCProjects = useMemo(() => {
        const searchKey = bcProjectSearchKey?.toLowerCase().trim();

        if (!searchKey) {
            return projects ?? [];
        }

        return (
            projects?.filter((project) => {
                const jobNumber = String(project?.job_number ?? "").toLowerCase();
                const address = String(project?.address ?? "").toLowerCase();

                return (
                    jobNumber.includes(searchKey) ||
                    address.includes(searchKey)
                );
            }) ?? []
        );
    }, [projects, bcProjectSearchKey]);


    const filteredEmployees = useMemo(() => {
        const searchKey = employeeSearchKey?.toLowerCase().trim();

        if (!searchKey) {
            return employees ?? [];
        }

        return (
            employees?.filter((employee) => {
                const firstName = String(employee?.first_name ?? "").toLowerCase();
                const lastName = String(employee?.last_name ?? "").toLowerCase();

                const fullName = `${firstName} ${lastName}`.trim();
                const reverseFullName = `${lastName} ${firstName}`.trim();
                const email = String(employee?.email ?? "").toLowerCase();

                return (
                    fullName.includes(searchKey) ||
                    reverseFullName.includes(searchKey) ||
                    email.includes(searchKey)
                );
            }) ?? []
        );
    }, [employees, employeeSearchKey]);

    const handleGetBuildingControlPermissions = useCallback(async () => {
        try {
            await dispatch(fetchBuildingControlPermissions()).unwrap();
        } catch (error) {
            toast?.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to fetch building control permissions.",
                life: 3000,
            });
        } finally {
            setInitialDataLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchAllProjects());
        dispatch(fetchAllContact());
        handleGetBuildingControlPermissions();
    }, [
        dispatch,
        handleGetBuildingControlPermissions,
    ]);


    // ------------------------------------------- BC Project
    const handleAddBuildingControlProject = () => {
        setShowBCProjectModal(true);
    }

    const handleCloseBCProjectModal = () => {
        setShowBCProjectModal(false);
        setSelectedBCProjects([]);
    }

    const addBCProjects = async (e: React.FormEvent) => {
        e.preventDefault();

        if (selectedBCProjects?.length === 0) {
            toast?.current?.show({
                severity: "warn",
                summary: "Warning",
                detail: "Select a project to add.",
                life: 3000,
            });

            return;
        };

        setLoading(true);

        try {
            const list = selectedBCProjects?.map((e) => e.project_id) ?? [];

            const response = await addBuildingControlProjectsAPI(list);

            if (response.statusCode === 200) {
                await handleGetBuildingControlPermissions();
                handleCloseBCProjectModal();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Project(s) added successfully.",
                    life: 3000,
                });
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error adding projects.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error while adding projects:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while adding projects.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    const handleSelectBCProject = (project: Project, isChecked: boolean) => {
        if (isChecked) {
            setSelectedBCProjects((prevState) => [...prevState, project]);
        } else {
            setSelectedBCProjects((prevState) =>
                prevState.filter(
                    (existingProject) => existingProject.project_id !== project.project_id
                )
            );
        }
    };

    const isSelectedBCProject = (project: Project): boolean =>
        selectedBCProjects.some(
            (existingProject) => existingProject.project_id === project.project_id
        );

    const buildingControlProjectModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handleCloseBCProjectModal}
            />

            <Button
                label={loading ? 'Processing...' : `Add${selectedBCProjects?.length > 0 ? `(${String(selectedBCProjects?.length).padStart(2, '0')})` : ''}`}
                onClick={addBCProjects}
                className="custom_btn primary"
                loading={loading}
                disabled={selectedBCProjects?.length === 0}
            />
        </div>
    );

    const handleRemoveBCProject = (bcProject: BuildingControlPermissionType | null) => {
        if (!bcProject) return;

        const projectName = bcProject?.project_detail?.job_number + ': ' + bcProject?.project_detail?.full_address;

        confirmDialog({
            message: () =>
                <span>
                    Are you sure you want to remove the project "<span className="text_bold text-capitalize">{projectName}</span>" from the building control permission?
                    <span className="first_letter_capitalized d-block mt-2"> This action cannot be undone. Additionally, it will remove the project from the building control permission and remove the assigned personnel and the access granted to them.</span>
                </span>
            ,
            header: "Confirm the project removal",
            icon: "bi bi-info-circle",
            defaultFocus: "accept",
            acceptClassName: "p-button-danger",
            accept: () => removeBCProject(bcProject.id),
            dismissableMask: true,
            resizable: false,
            draggable: false,
        });
    }

    const removeBCProject = async (bcProjectId: number) => {
        if (!bcProjectId) return;

        setRemoveProjectLoadingIdx(bcProjectId);

        try {
            const response = await removeBuildingControlProjectsAPI(bcProjectId);

            if (response.statusCode === 200) {
                await handleGetBuildingControlPermissions();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Project removed successfully.",
                    life: 3000,
                });
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error removing the project.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error while removing the project:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while removing the project.",
                life: 3000,
            });
        } finally {
            setRemoveProjectLoadingIdx(null);
        }
    }
    // -------------------------------------------


    // ------------------------------------------- BC Employee
    const updateHeight = () => {
        if (employeeListBodyRef.current) {
            setBodyHeight(employeeListBodyRef.current.offsetHeight - 170);
        }
    };

    useEffect(() => {
        if (showEmployeeModal && employeeListBodyRef.current) {
            updateHeight();

            window.addEventListener("resize", updateHeight);

            return () => {
                window.removeEventListener("resize", updateHeight);
            };
        }
    }, [showEmployeeModal, employeeListBodyRef]);


    const handleAddBuildingControlEmployee = (projectPermission: BuildingControlPermissionType | null) => {
        if (!projectPermission) return

        setCurrentBCProjectPermission(projectPermission);
        setShowEmployeeModal(true);
    }

    const handleCloseEmployeeModal = () => {
        setShowEmployeeModal(false);
        setCurrentBCProjectPermission(null);
        setSelectedEmployees([]);
    }

    const assignBuildingControlEmployee = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!currentBCProjectPermission) return;

        if (selectedEmployees?.length === 0) {
            toast?.current?.show({
                severity: "warn",
                summary: "Warning",
                detail: "Select a employee to assign.",
                life: 3000,
            });

            return;
        };

        setLoading(true);

        try {
            const bcp_project_id = currentBCProjectPermission?.id;
            const list = selectedEmployees?.map((e) => e.id) ?? [];

            const response = await addBuildingControlEmployeesAPI(
                bcp_project_id,
                list
            );

            if (response.statusCode === 200) {
                await handleGetBuildingControlPermissions();
                handleCloseEmployeeModal();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Employee(s) assigned successfully.",
                    life: 3000,
                });
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error assigning employee.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error while assigning employee:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while assigning employee.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    const handleSelectEmployee = (employee: Employee, isChecked: boolean) => {
        if (isChecked) {
            setSelectedEmployees((prevState) => [...prevState, employee]);
        } else {
            setSelectedEmployees((prevState) =>
                prevState.filter(
                    (existingEmployee) => existingEmployee.id !== employee.id
                )
            );
        }
    };

    const isSelectedEmployee = (employee: Employee): boolean =>
        selectedEmployees.some(
            (existingEmployee) => existingEmployee.id === employee.id
        );

    const assignEmployeeModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">Assign employee</h5>
            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseEmployeeModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const assignEmployeeModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseEmployeeModal}
            />
            <Button
                label={loading ? 'Processing...' : `Assign${selectedEmployees?.length > 0 ? `(${String(selectedEmployees?.length).padStart(2, '0')})` : ''}`}
                loading={loading}
                onClick={assignBuildingControlEmployee}
                className="custom_btn primary"
                disabled={selectedEmployees?.length === 0}
            />
        </div>
    );

    const handleRemoveBCEmployee = (
        e: React.MouseEvent<HTMLButtonElement>,
        bcEmployee: BuildingControlPermissionEmployee | null
    ) => {
        e.preventDefault();
        e.stopPropagation();

        if (!bcEmployee) return;

        const employeeName = bcEmployee?.first_name + ' ' + bcEmployee?.last_name;

        confirmDialog({
            message: () =>
                <span>
                    Are you sure you want to remove the employee "<span className="text_bold text-capitalize">{employeeName}</span>" from this project?
                    <span className="first_letter_capitalized d-block mt-2"> This action cannot be undone. Furthermore, it will remove the employee from the project and cancel all permissions granted.</span>
                </span>
            ,
            header: "Confirm the employee removal",
            icon: "bi bi-info-circle",
            defaultFocus: "accept",
            acceptClassName: "p-button-danger",
            accept: () => removeBCEmployee(bcEmployee.bcp_employee_id),
            dismissableMask: true,
            resizable: false,
            draggable: false,
        });
    }

    const removeBCEmployee = async (bcEmployeeId: number) => {
        if (!bcEmployeeId) return;

        setRemoveEmployeeLoadingIdx(bcEmployeeId);

        try {
            const response = await removeBuildingControlEmployeeAPI(bcEmployeeId);

            if (response.statusCode === 200) {
                await handleGetBuildingControlPermissions();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Employee removed successfully.",
                    life: 3000,
                });
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error removing the employee.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error while removing the employee:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while removing the employee.",
                life: 3000,
            });
        } finally {
            setRemoveEmployeeLoadingIdx(null);
        }
    }
    // -------------------------------------------


    // ------------------------------------------- BC Permission

    // Build the initial draft map for one employee from their service list.
    const buildDraftForEmployee = (employee: BuildingControlPermissionEmployee): Map<number, { can_view: 1 | 0; can_edit: 1 | 0 }> => {
        const map = new Map<number, { can_view: 1 | 0; can_edit: 1 | 0 }>();

        employee.added_service_list?.forEach((svc) => {
            map.set(svc.id, { can_view: svc.can_view, can_edit: svc.can_edit });
        });

        return map;
    };

    // Initialise draft for an employee if not already present.
    const ensureDraftForEmployee = (employee: BuildingControlPermissionEmployee) => {
        setPermissionDraft((prev) => {
            if (prev.has(employee.bcp_employee_id)) return prev;
            const next = new Map(prev);
            next.set(employee.bcp_employee_id, buildDraftForEmployee(employee));
            return next;
        });
    };

    const handleTogglePermissionEmployee = (employee: BuildingControlPermissionEmployee) => {
        setActivePermissionEmployee((prevSelected) => {
            if (prevSelected?.bcp_employee_id === employee.bcp_employee_id) return null;
            return employee;
        });
        // Seed draft outside the setter callback to avoid stale-closure issues.
        ensureDraftForEmployee(employee);
        goToTop();
    };

    // Seed drafts when data first loads — expand first employee of first project.
    useEffect(() => {
        if (!buildingControlPermissions || buildingControlPermissions.length === 0) return;
        const firstProject = buildingControlPermissions[0];
        const firstEmployee = firstProject?.employee_list?.[0];
        if (firstEmployee) {
            ensureDraftForEmployee(firstEmployee);
            setActivePermissionEmployee(firstEmployee);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildingControlPermissions?.length]);

    // View checkbox toggle.
    const handleViewChange = (employee: BuildingControlPermissionEmployee, serviceId: number, checked: boolean) => {
        const empId = employee.bcp_employee_id;
        setPermissionDraft((prev) => {
            const next = new Map(prev);
            const empMap = new Map(next.get(empId) ?? buildDraftForEmployee(employee));
            const current = empMap.get(serviceId) ?? { can_view: 0, can_edit: 0 };
            // Unchecking view also unchecks edit.
            empMap.set(serviceId, {
                can_view: checked ? 1 : 0,
                can_edit: checked ? current.can_edit : 0,
            });
            next.set(empId, empMap);
            return next;
        });
    };

    // Edit checkbox toggle — edit requires view.
    const handleEditChange = (employee: BuildingControlPermissionEmployee, serviceId: number, checked: boolean) => {
        const empId = employee.bcp_employee_id;
        setPermissionDraft((prev) => {
            const next = new Map(prev);
            const empMap = new Map(next.get(empId) ?? buildDraftForEmployee(employee));
            const current = empMap.get(serviceId) ?? { can_view: 0, can_edit: 0 };
            empMap.set(serviceId, {
                can_view: checked ? 1 : current.can_view, // enabling edit auto-enables view
                can_edit: checked ? 1 : 0,
            });
            next.set(empId, empMap);
            return next;
        });
    };

    // Grant all permissions (view + edit) for every service of an employee.
    const handleGrantAllPermissions = (employee: BuildingControlPermissionEmployee) => {
        const empId = employee.bcp_employee_id;
        setPermissionDraft((prev) => {
            const next = new Map(prev);
            const empMap = new Map(next.get(empId) ?? buildDraftForEmployee(employee));
            employee.added_service_list?.forEach((svc) => {
                empMap.set(svc.id, { can_view: 1, can_edit: 1 });
            });
            next.set(empId, empMap);
            return next;
        });
    };

    // Revoke all permissions (view + edit) for every service of an employee.
    const handleRevokeAllPermissions = (employee: BuildingControlPermissionEmployee) => {
        const empId = employee.bcp_employee_id;
        setPermissionDraft((prev) => {
            const next = new Map(prev);
            const empMap = new Map(next.get(empId) ?? buildDraftForEmployee(employee));
            employee.added_service_list?.forEach((svc) => {
                empMap.set(svc.id, { can_view: 0, can_edit: 0 });
            });
            next.set(empId, empMap);
            return next;
        });
    };

    // Computed comparison: true only when the draft actually differs from the original API data.
    // This means buttons appear on any real change and disappear when all changes are undone.
    const employeeHasChanges = (employee: BuildingControlPermissionEmployee): boolean => {
        const empDraft = permissionDraft.get(employee.bcp_employee_id);
        if (!empDraft) return false;
        return employee.added_service_list.some((svc) => {
            const draft = empDraft.get(svc.id);
            if (!draft) return false;
            return draft.can_view !== svc.can_view || draft.can_edit !== svc.can_edit;
        });
    };

    const handleSavePermissionChanges = async (
        e: React.MouseEvent<HTMLButtonElement>,
        employee: BuildingControlPermissionEmployee
    ) => {
        e.preventDefault();
        e.stopPropagation();

        if (!employee) return;

        setSaveLoading(true);

        try {
            const employeeName = `${employee.first_name} ${employee.last_name}`;
            const bcp_employee_id = employee.bcp_employee_id;

            // Build service_list: include only services where can_view === 1.
            // add_default_id = service.id, can_edit = 1|0
            const empDraft = permissionDraft.get(bcp_employee_id);
            const service_list: BuildingControlPermissionPayload[] = [];

            employee.added_service_list?.forEach((svc) => {
                const draft = empDraft?.get(svc.id);
                const canView = draft ? draft.can_view : svc.can_view;
                const canEdit = draft ? draft.can_edit : svc.can_edit;
                if (canView === 1) {
                    service_list.push({
                        add_default_id: svc.id,
                        can_edit: canEdit,
                    });
                }
                // Services with can_view === 0 are simply omitted from the list.
            });

            const response = await addBuildingControlPermissionAPI(bcp_employee_id, service_list);

            if (response.statusCode === 200) {
                // Clear draft so it re-seeds from fresh API data.
                setPermissionDraft((prev) => {
                    const next = new Map(prev);
                    next.delete(bcp_employee_id);
                    return next;
                });

                await handleGetBuildingControlPermissions();

                // Re-expand the same employee after data refreshes.
                setActivePermissionEmployee(employee);

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: `Permissions updated for ${employeeName}.`,
                    life: 3000,
                });
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: `There was an error updating permissions for ${employeeName}.`,
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Failed to save permission changes:", error);
            toast?.current?.show({
                severity: "error",
                summary: "Failed",
                detail: "Something went wrong while updating permissions.",
                life: 3000,
            });
        } finally {
            setSaveLoading(false);
        }
    };

    const handleCancelPermissionChanges = (
        e: React.MouseEvent<HTMLButtonElement>,
        employee: BuildingControlPermissionEmployee
    ) => {
        e.preventDefault();
        e.stopPropagation();

        // Reset this employee's draft back to original API values.
        // employeeHasChanges is computed — resetting draft automatically hides action buttons.
        setPermissionDraft((prev) => {
            const next = new Map(prev);
            next.set(employee.bcp_employee_id, buildDraftForEmployee(employee));
            return next;
        });
    };

    const employeePermissionActionsBody = (employee: BuildingControlPermissionEmployee) => {
        const isEmployeeRemoveLoading = removeEmployeeLoadingIdx === employee?.bcp_employee_id;
        const isActive = employee?.bcp_employee_id === activePermissionEmployee?.bcp_employee_id;
        const showPermissionAction = isActive && employeeHasChanges(employee) && hasPermissionForAddBuildingControlPermission

        return (
            <div className="table_action_btn_area justify-content-end">
                <Tooltip target={`.removeEmployee${employee?.bcp_employee_id}`} />

                {showPermissionAction && (
                    <>
                        <button
                            type="button"
                            className="table_action_btn white success p-ripple"
                            onClick={(e) => handleSavePermissionChanges(e, employee)}
                            disabled={saveLoading}
                        >
                            {saveLoading ? (
                                <span className="d-flex align-items-center justify-content-center gap-2">
                                    <TableActionLoader width={18} height={18} />
                                    Processing...
                                </span>
                            ) : (
                                <span>Save Changes</span>
                            )}
                            <Ripple />
                        </button>

                        <button
                            type="button"
                            className="table_action_btn white secondary p-ripple"
                            onClick={(e) => handleCancelPermissionChanges(e, employee)}
                            disabled={saveLoading}
                        >
                            Cancel
                            <Ripple />
                        </button>
                    </>
                )}

                {!showPermissionAction && hasPermissionForRemoveBuildingControlEmployee && (
                    <button
                        type="button"
                        className={`table_action_btn white danger p-ripple removeEmployee${employee?.bcp_employee_id}`}
                        onClick={(e) => handleRemoveBCEmployee(e, employee)}
                        disabled={isEmployeeRemoveLoading}
                        data-pr-tooltip="Remove Employee"
                        data-pr-position="left"
                    >
                        {isEmployeeRemoveLoading ? (
                            <span className="d-flex align-items-center justify-content-center gap-2">
                                <TableActionLoader width={18} height={18} />
                                Processing...
                            </span>
                        ) : (
                            <span>Remove</span>
                        )}
                        <Ripple />
                    </button>
                )}
            </div>
        );
    };
    // -------------------------------------------

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingBuildingControlPermissions && initialDataLoading ? (
                    <PageLoader />
                ) : (errorBuildingControlPermissions) ? (
                    <ErrorPage errorMessage={errorBuildingControlPermissions ?? ''} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="d-flex align-items-center justify-content-between">
                            <h4 className="main_heading">Building control permission </h4>

                            <GoBackButton />
                        </div>

                        <div className="page_header_area justify-content-end mt-sm-3 mt-3">
                            {hasPermissionForAddBuildingControlProject && (
                                <div className="page_action_area">
                                    <button
                                        className="add_data_link_btn p-ripple"
                                        onClick={handleAddBuildingControlProject}
                                    >
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add project</span>
                                        <Ripple />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="page_main_content_section ">
                            {loadingBuildingControlPermissions && (
                                <CustomTableLoader />
                            )}

                            {buildingControlPermissions && buildingControlPermissions?.length > 0 ? (
                                <React.Fragment>
                                    {buildingControlPermissions?.map((bcProject) => {
                                        const isProjectRemoveLoading = removeProjectLoadingIdx === bcProject?.id;

                                        return (
                                            <div key={`bcp_${bcProject?.id}`} className="page_sub_content_section">
                                                <div className="page_sub_content_header custom">
                                                    <div className="page_sub_content_sub">
                                                        <h4 className="page_sub_content_head text-capitalize">
                                                            <i className="bi bi-buildings me-2"></i>
                                                            {bcProject?.project_detail?.job_number} : {bcProject?.project_detail?.full_address}
                                                        </h4>
                                                    </div>

                                                    <div className="page_sub_content_header_btn_area">
                                                        {hasPermissionForAssignBuildingControlEmployee && (
                                                            <TableButton
                                                                label="Assign employee"
                                                                icon="bi bi-person-add"
                                                                iconClassName="me-2"
                                                                className="radiused"
                                                                onClick={() => handleAddBuildingControlEmployee(bcProject)}
                                                            />
                                                        )}

                                                        {hasPermissionForRemoveBuildingControlProject && (
                                                            <TableButton
                                                                icon="bi bi-trash"
                                                                className="danger radiused icon_only"
                                                                tooltip="Remove project"
                                                                tooltipPosition="left"
                                                                loading={isProjectRemoveLoading}
                                                                disabled={isProjectRemoveLoading}
                                                                tooltipTarget={`bcProject_${bcProject?.id}`}
                                                                onClick={() => handleRemoveBCProject(bcProject)}
                                                            />
                                                        )}

                                                        <span className="sub_content_badge">
                                                            {String(bcProject?.employee_list?.length ?? 0).padStart(2, '0')}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="page_sub_content_body">
                                                    {bcProject?.employee_list?.length > 0 ? (
                                                        <div className="pck_track_list_area">
                                                            <div className="table-responsive">
                                                                <table className="table pck_track_list_table">
                                                                    <tbody>
                                                                        {bcProject?.employee_list?.map((employee, index) => {
                                                                            const isActive = employee?.bcp_employee_id === activePermissionEmployee?.bcp_employee_id;

                                                                            return (
                                                                                <React.Fragment>
                                                                                    <tr
                                                                                        key={`employee_${employee?.id}`}
                                                                                        onClick={() => handleTogglePermissionEmployee(employee)}
                                                                                        className={`part_row ${isActive ? 'active' : ''}`}
                                                                                    >
                                                                                        <td
                                                                                            style={{ width: "3rem" }}
                                                                                            className="text-center">
                                                                                            <i className={`bi bi-chevron-right ${isActive ? 'toggled' : ''} data_toggle_icon`}></i>
                                                                                        </td>

                                                                                        <td style={{ width: '72%' }} className="text-capitalize text_no_wrap">
                                                                                            <span className="text_bold">
                                                                                                {String(index + 1).padStart(2, '0') + ". "}
                                                                                                {employee?.first_name + " " + employee?.last_name}</span> | <span className="text_subtext lower">{employee?.email}
                                                                                            </span>
                                                                                        </td>

                                                                                        <td style={{ width: "25%" }}>
                                                                                            {employeePermissionActionsBody(employee)}
                                                                                        </td>
                                                                                    </tr>

                                                                                    <AnimatePresence initial={false}>
                                                                                        {isActive && (
                                                                                            <tr className="part_item_row">
                                                                                                <td colSpan={3} style={{ width: '100%' }} className='p-0'>
                                                                                                    <motion.div
                                                                                                        initial={{ height: 0, opacity: 0 }}
                                                                                                        animate={{ height: 'auto', opacity: 1 }}
                                                                                                        exit={{ height: 0, opacity: 0 }}
                                                                                                        transition={{ duration: 0.3, ease: [0.3, 0, 0.2, 1] }}
                                                                                                        style={{ overflow: 'hidden' }}
                                                                                                    >
                                                                                                        <div className="item_list_wrapper">
                                                                                                            <div className="pck_track_list_item_area">
                                                                                                                <div className="bc_permission_list_container">
                                                                                                                    {employee?.added_service_list?.length > 0 ? (
                                                                                                                        <>
                                                                                                                            {/* Grant All / Revoke All quick-action bar */}
                                                                                                                            {hasPermissionForAddBuildingControlPermission && (
                                                                                                                                <div className="bc_permission_bulk_actions">
                                                                                                                                    <span className="bc_permission_bulk_label">Quick actions:</span>
                                                                                                                                    <button
                                                                                                                                        type="button"
                                                                                                                                        className="bc_permission_bulk_btn grant p-ripple"
                                                                                                                                        onClick={() => handleGrantAllPermissions(employee)}
                                                                                                                                        title="Grant view and edit access to all services"
                                                                                                                                    >
                                                                                                                                        <i className="bi bi-shield-check"></i>
                                                                                                                                        <span>Grant All</span>
                                                                                                                                        <Ripple />
                                                                                                                                    </button>
                                                                                                                                    <button
                                                                                                                                        type="button"
                                                                                                                                        className="bc_permission_bulk_btn revoke p-ripple"
                                                                                                                                        onClick={() => handleRevokeAllPermissions(employee)}
                                                                                                                                        title="Revoke all access from all services"
                                                                                                                                    >
                                                                                                                                        <i className="bi bi-shield-x"></i>
                                                                                                                                        <span>Revoke All</span>
                                                                                                                                        <Ripple />
                                                                                                                                    </button>
                                                                                                                                </div>
                                                                                                                            )}

                                                                                                                            <div className="row bc_permission_list_row">
                                                                                                                            {employee?.added_service_list?.map((service, index) => {
                                                                                                                                const empDraft = permissionDraft.get(employee.bcp_employee_id);
                                                                                                                                const draftEntry = empDraft?.get(service.id);
                                                                                                                                const canView = draftEntry ? draftEntry.can_view : service.can_view;
                                                                                                                                const canEdit = draftEntry ? draftEntry.can_edit : service.can_edit;

                                                                                                                                return (
                                                                                                                                    <div className="col-12 col-sm-6 col-md-4 px-2" key={`project_${bcProject?.id}_employee_${employee?.id}_service_${service?.id}`}>
                                                                                                                                        <div className={`bc_permission_item ${canView === 1 ? 'has_access' : ''}`}>
                                                                                                                                            <div className="bc_permission_item_header">
                                                                                                                                                <span className="bc_permission_item_num">{String(index + 1).padStart(2, '0')}</span>
                                                                                                                                                <h6 className="bc_permission_title">{service?.feature_name}</h6>
                                                                                                                                            </div>

                                                                                                                                            <div className="bc_permission">
                                                                                                                                                <div
                                                                                                                                                    className={`bc_permission_group ${canView === 1 ? 'active' : ''}`}
                                                                                                                                                    onClick={() => handleViewChange(employee, service.id, canView !== 1)}
                                                                                                                                                    role="button"
                                                                                                                                                    aria-label={`Toggle view for ${service.feature_name}`}
                                                                                                                                                >
                                                                                                                                                    <Checkbox
                                                                                                                                                        inputId={`view_${employee.bcp_employee_id}_${service.id}`}
                                                                                                                                                        checked={canView === 1}
                                                                                                                                                        onChange={(e) => handleViewChange(employee, service.id, e.checked ?? false)}
                                                                                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                                                                                        className="checkbox_success circle"
                                                                                                                                                    />
                                                                                                                                                    <span className="bc_permission_access">View</span>
                                                                                                                                                </div>

                                                                                                                                                <div
                                                                                                                                                    className={`bc_permission_group ${canEdit === 1 ? 'active' : ''}`}
                                                                                                                                                    onClick={() => handleEditChange(employee, service.id, canEdit !== 1)}
                                                                                                                                                    role="button"
                                                                                                                                                    aria-label={`Toggle edit for ${service.feature_name}`}
                                                                                                                                                >
                                                                                                                                                    <Checkbox
                                                                                                                                                        inputId={`edit_${employee.bcp_employee_id}_${service.id}`}
                                                                                                                                                        checked={canEdit === 1}
                                                                                                                                                        onChange={(e) => handleEditChange(employee, service.id, e.checked ?? false)}
                                                                                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                                                                                        className="checkbox_success circle"
                                                                                                                                                    />
                                                                                                                                                    <span className="bc_permission_access">Edit</span>
                                                                                                                                                </div>
                                                                                                                                            </div>
                                                                                                                                        </div>
                                                                                                                                    </div>
                                                                                                                                );
                                                                                                                            })}
                                                                                                                            </div>
                                                                                                                        </>
                                                                                                                    ) : (
                                                                                                                        <DataException
                                                                                                                            message={`There are no building control services found.`}
                                                                                                                        />
                                                                                                                    )}
                                                                                                                </div>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    </motion.div>
                                                                                                </td>
                                                                                            </tr>
                                                                                        )}
                                                                                    </AnimatePresence>
                                                                                </React.Fragment>
                                                                            )
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <NoDataCreated
                                                            containerClassname="pos_rel sub"
                                                            message={`No employees have been assigned to this project yet. ${hasPermissionForAssignBuildingControlEmployee ? "To assign an employee, click the 'Assign employee' button." : ""}`}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </React.Fragment>
                            ) : (
                                <NoDataCreated message={`No projects added yet. ${hasPermissionForAddBuildingControlProject ? 'To add projects click on Add project button.' : ''}`} />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Building control project modal */}
            <Dialog
                visible={showBCProjectModal}
                footer={buildingControlProjectModalFooter}
                headerClassName="d-none"
                dismissableMask
                draggable={false}
                className="custom_modal_dialog modal_dialog_sm data_select_modal"
                contentClassName="data_select_modal_content"
                onHide={handleCloseBCProjectModal}
            >
                <div className="custom_modal_body data_select_modal_area">
                    <div className="data_select_modal_header">
                        <DataListModalSearch
                            key={`bcProjectSearchKey`}
                            inputId="bcProjectSearchKey"
                            value={bcProjectSearchKey}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBcProjectSearchKey(e.target.value)}
                            placeholder="Search project by job number/address"
                            disabled={projects?.length === 0}
                            onClear={() => setBcProjectSearchKey("")}
                        />

                        <DataListModalClose
                            onClose={handleCloseBCProjectModal}
                        />
                    </div>

                    {loadingProjects ? (
                        <CustomTableLoader />
                    ) : errorProjects ? (
                        <div className="data_select_modal_body custom">
                            <DataException
                                containerClassName="custom_height"
                                message={errorProjects}
                            />
                        </div>
                    ) : (
                        <div className="data_select_modal_body custom">
                            {filteredBCProjects && filteredBCProjects?.length > 0 ? (
                                <div className="data_select_option_area">
                                    {filteredBCProjects?.map((project,) => {
                                        const isAlreadyAdded = buildingControlPermissions?.some((permission) => permission?.project_id === project?.project_id);

                                        return (
                                            <div key={`bcProject${project?.project_id}`} className="data_select_option_check_area">
                                                <Checkbox
                                                    onChange={(e) => handleSelectBCProject(project, e.checked || false)}
                                                    checked={isSelectedBCProject(project) || isAlreadyAdded}
                                                    disabled={isAlreadyAdded}
                                                ></Checkbox>

                                                <div
                                                    className={`data_select_option p-ripple 
                                                        ${isSelectedBCProject(project) ? "selected" : ""}
                                                        ${isAlreadyAdded ? "disabled" : ""}
                                                    `}
                                                    onClick={() => {
                                                        if (isAlreadyAdded) return;
                                                        handleSelectBCProject(project, !isSelectedBCProject(project));
                                                    }}
                                                >
                                                    <span>
                                                        {project?.job_number}: {project?.address}

                                                        {isAlreadyAdded && (
                                                            <small className="ms-2 text-danger">
                                                                (Already added)
                                                            </small>
                                                        )}
                                                    </span>
                                                    <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            ) : (
                                <DataException
                                    containerClassName="custom_height"
                                    message={bcProjectSearchKey ? 'The project you were looking for is not found!' : `No projects found.`}
                                />
                            )}
                        </div>
                    )}
                </div>
            </Dialog>

            {/* Building control employee assign modal */}
            <Dialog
                visible={showEmployeeModal}
                header={assignEmployeeModalHeader}
                footer={assignEmployeeModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm assign_contact_modal"
                onHide={handleCloseEmployeeModal}
                dismissableMask
                draggable={false}
            >
                <div className="custom_modal_body fixed_height" ref={employeeListBodyRef}>
                    {loadingEmployees ? (
                        <CustomTableLoader />
                    ) : errorEmployees ? (
                        <div className="data_select_modal_body custom">
                            <DataException
                                containerClassName="h-100"
                                message={errorEmployees}
                            />
                        </div>
                    ) : (
                        <>
                            <div className="pt-0 px-sm-0 px-1 pb-2 w-100">
                                <div className="share_contact_tab_header">
                                    <div className="data_search_input_area contact_search mt-0">
                                        <InputText
                                            key={"employeeSearchKey"}
                                            id="employeeSearchKey"
                                            value={employeeSearchKey}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmployeeSearchKey(e.target.value)}
                                            className="custom_form_input data_search_input"
                                            placeholder="Search employee by name/email"
                                            autoComplete="off"
                                            autoFocus
                                            type={"search"}
                                        />
                                        {employeeSearchKey === "" ? (
                                            <i className="bi bi-search"></i>
                                        ) : (
                                            <i
                                                className="bi bi-x-lg data_search_input_clear"
                                                onClick={() => setEmployeeSearchKey('')}
                                            ></i>
                                        )}
                                    </div>
                                </div>

                                <div className="row">
                                    {filteredEmployees?.length > 0 ? (
                                        <div className="col-12">
                                            <ul className="assign_contact_list_area mt-sm-2 ps-sm-4 pe-sm-4 mb-sm-3 mb-2">
                                                {filteredEmployees.map((employee) => {
                                                    const isAlreadyAssigned = (currentBCProjectPermission && currentBCProjectPermission?.employee_list?.some((assign) => assign?.id === employee?.id)) ?? false;

                                                    return (
                                                        <li className="assign_contact_list w-100">
                                                            <Checkbox
                                                                onChange={(e) => handleSelectEmployee(employee, e.checked || false)}
                                                                checked={isSelectedEmployee(employee) || isAlreadyAssigned}
                                                                disabled={isAlreadyAssigned}
                                                            ></Checkbox>

                                                            <button
                                                                className={`contact_slide_tab assign_contact p-ripple 
                                                            ${isSelectedEmployee(employee) ? "active" : ""}
                                                            ${isAlreadyAssigned ? "disabled" : ""}
                                                        `}
                                                                onClick={() => {
                                                                    if (isAlreadyAssigned) return;
                                                                    handleSelectEmployee(employee, !isSelectedEmployee(employee));
                                                                }}
                                                            >
                                                                <Image
                                                                    className="contact_profile_img"
                                                                    src={employee?.profile_image}
                                                                    alt="Profile"
                                                                />
                                                                <div className="contact_slide_det w-100">
                                                                    <h6>
                                                                        {employee?.first_name + " " + employee?.last_name}
                                                                    </h6>

                                                                    {employee.contactSubType && (
                                                                        <p>{employee.contactSubType}</p>
                                                                    )}

                                                                    {employee?.email && (
                                                                        <small>
                                                                            {employee?.email}
                                                                        </small>
                                                                    )}

                                                                    {isAlreadyAssigned && (
                                                                        <small className="sub_text text-danger">
                                                                            (Already assigned)
                                                                        </small>
                                                                    )}
                                                                </div>
                                                                <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                                            </button>
                                                        </li>
                                                    )
                                                })}
                                            </ul>
                                        </div>
                                    ) : (
                                        <div className="col-12">
                                            <div className="no_data_found d-flex align-content-center flex-column justify-content-center mt-2 mb-2"
                                                style={{ height: bodyHeight }}>
                                                <h6>
                                                    {employeeSearchKey
                                                        ? `No employee(s) found matching "${employeeSearchKey}".`
                                                        : "No employee(s) have been created yet. Go to Contacts to create, then assign employee to the project."
                                                    }
                                                </h6>

                                                {employees?.length === 0 && (
                                                    <button
                                                        className={`add_data_btn p-ripple mt-3 mx-auto`}
                                                        onClick={() => navigate('/contact')}
                                                    >
                                                        <i className={`bi bi-box-arrow-up-left`}></i>
                                                        Contacts
                                                        <Ripple />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </Dialog>
        </React.Fragment>
    );
};

export default BuildingControlPermission;